import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Users, FileText, Settings, Crown, Palette } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CSOLoadingSpinner } from "@/components/ui/cso-loading-spinner";
import { SeatManagement } from "./SeatManagement";
import { EnhancedMemberInvitations } from "./EnhancedMemberInvitations";
import { OrganizationBranding } from "./OrganizationBranding";
import { WhiteLabelSettings } from "./WhiteLabelSettings";
import { UsageAnalytics } from "./UsageAnalytics";
import { EnterpriseIntegrations } from "./EnterpriseIntegrations";

interface Organization {
  id: string;
  name: string;
  tier: string;
  max_seats: number;
  subdomain?: string;
  stripe_subscription_id?: string;
}

interface OrganizationMember {
  id: string;
  user_id: string;
  role: string;
  joined_at: string;
  profiles: {
    full_name: string;
    email: string;
  } | null;
}

export const OrganizationDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<string>('');

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  const fetchOrganizationData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No authenticated user found');
        navigate('/auth');
        return;
      }

      console.log('Fetching organization data for user:', user.id);

      // Get user's organization membership with better error handling
      const { data: membership, error: membershipError } = await supabase
        .from('organization_memberships')
        .select(`
          role,
          organization_id,
          organizations (
            id, name, tier, max_seats, subdomain, stripe_subscription_id
          )
        `)
        .eq('user_id', user.id)
        .maybeSingle();

      if (membershipError) {
        console.error('Error fetching membership:', membershipError);
        toast({
          title: "Access Error",
          description: "Failed to load organization data. Please try again.",
          variant: "destructive",
        });
        navigate('/dashboard');
        return;
      }

      if (!membership || !membership.organizations) {
        console.log('No organization membership found for user');
        toast({
          title: "No Organization Access",
          description: "You don't have access to any organization. Create one from your settings.",
          variant: "destructive",
        });
        navigate('/settings');
        return;
      }

      console.log('Organization membership found:', membership);
      setUserRole(membership.role);
      setOrganization(membership.organizations as Organization);

      // Get all organization members with proper join
      const { data: membersData, error: membersError } = await supabase
        .from('organization_memberships')
        .select(`
          id, user_id, role, joined_at,
          profiles!inner (full_name, email)
        `)
        .eq('organization_id', membership.organization_id);

      if (membersError) {
        console.error('Error fetching members:', membersError);
        // Set empty array if there's an error to prevent crashes
        setMembers([]);
      } else {
        // Transform the data to match our interface
        const transformedMembers = membersData?.map((member: any) => ({
          id: member.id,
          user_id: member.user_id,
          role: member.role,
          joined_at: member.joined_at,
          profiles: member.profiles
        })) || [];
        console.log('Organization members loaded:', transformedMembers);
        setMembers(transformedMembers);
      }

    } catch (error) {
      console.error('Error fetching organization data:', error);
      toast({
        title: "Error",
        description: "Failed to load organization data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'enterprise':
        return 'bg-purple-100 text-purple-800';
      case 'professional':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isAdmin = userRole === 'owner' || userRole === 'admin';

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CSOLoadingSpinner size="lg" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-6 text-center">
          <p>Organization not found</p>
          <Button onClick={() => navigate('/settings')} className="mt-4">
            Go to Settings
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{organization.name}</h1>
              <div className="flex items-center gap-2 mt-2">
                <Badge className={getTierBadgeColor(organization.tier)}>
                  {organization.tier.charAt(0).toUpperCase() + organization.tier.slice(1)}
                </Badge>
                <span className="text-sm text-gray-500">
                  {members.length} / {organization.max_seats === -1 ? '∞' : organization.max_seats} seats
                </span>
              </div>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Members</p>
                  <p className="text-2xl font-bold">{members.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Documents</p>
                  <p className="text-2xl font-bold">-</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Crown className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Plan</p>
                  <p className="text-lg font-bold capitalize">{organization.tier}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Palette className="h-8 w-8 text-orange-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">White-Label</p>
                  <p className="text-sm font-bold">
                    {organization.tier === 'enterprise' ? 'Available' : 'Upgrade'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="integrations">Integrations</TabsTrigger>
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="whitelabel">White-Label</TabsTrigger>
            <TabsTrigger value="settings" disabled={!isAdmin}>Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">No recent activity</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Organization Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Subdomain</p>
                    <p className="text-sm">
                      {organization.subdomain ? 
                        `${organization.subdomain}.csoagent.com` : 
                        'Not configured'
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Subscription</p>
                    <p className="text-sm">
                      {organization.stripe_subscription_id ? 
                        'Active subscription' : 
                        'No active subscription'
                      }
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="members">
            <div className="space-y-6">
              {isAdmin && <EnhancedMemberInvitations organizationId={organization.id} />}
              <SeatManagement 
                organizationId={organization.id}
                maxSeats={organization.max_seats}
                currentUserRole={userRole}
                onMembersUpdate={fetchOrganizationData}
              />
            </div>
          </TabsContent>

          <TabsContent value="analytics">
            <UsageAnalytics organizationId={organization.id} />
          </TabsContent>

          <TabsContent value="integrations">
            <EnterpriseIntegrations organizationId={organization.id} />
          </TabsContent>

          <TabsContent value="branding">
            <OrganizationBranding organizationId={organization.id} />
          </TabsContent>

          <TabsContent value="whitelabel">
            <WhiteLabelSettings 
              organizationId={organization.id} 
              currentTier={organization.tier}
            />
          </TabsContent>

          <TabsContent value="settings">
            {isAdmin && (
              <Card>
                <CardHeader>
                  <CardTitle>Organization Settings</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Organization settings will be available soon.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
